import { useState, useEffect, useMemo, CSSProperties } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../amplify/data/resource';
import {
  Card, Btn, Badge, Select, Modal,
  SectionHeader, Table, Empty, Toast, Stat,
} from './ui';
import { apiPost } from '../lib/api';

// ─── Filter types ─────────────────────────────────────────────────────────────

const SENIORITY_OPTIONS = [
  'Founder', 'Owner', 'C-Suite', 'Partner',
  'Vice President', 'Director', 'Manager',
];
const HEADCOUNT_OPTIONS = [
  '1-10', '11-50', '51-100', '101-200',
  '201-500', '501-1000', '1001-2000', '2001-5000', '5001-10000', '10001+',
];
const COUNTRY_OPTIONS = [
  { code: 'US', label: 'United States' },
  { code: 'CA', label: 'Canada' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'AU', label: 'Australia' },
  { code: 'DE', label: 'Germany' },
  { code: 'FR', label: 'France' },
  { code: 'NL', label: 'Netherlands' },
  { code: 'IE', label: 'Ireland' },
];

const DEFAULT_FILTERS = {
  jobTitles:  [] as string[],
  exactMatch: false,
  seniority:  [] as string[],
  industries: [] as string[],
  headcount:  [] as string[],
  countries:  [] as string[],
};
type Filters = typeof DEFAULT_FILTERS;

function buildProspeoFilters(f: Filters): object {
  const out: any = {};
  if (f.jobTitles.length)
    out.person_job_title = { include: f.jobTitles, match_only_exact_job_titles: f.exactMatch };
  if (f.seniority.length)
    out.person_seniority = { include: f.seniority };
  if (f.industries.length)
    out.company_industry = { include: f.industries };
  if (f.headcount.length)
    out.company_headcount_range = f.headcount;
  if (f.countries.length)
    out.person_country = { include: f.countries };
  return out;
}

function parseProspeoFilters(json: string | null | undefined): Filters {
  if (!json) return { ...DEFAULT_FILTERS, seniority: [], jobTitles: [], industries: [], headcount: [], countries: [] };
  try {
    const f = typeof json === 'string' ? JSON.parse(json) : json;
    return {
      jobTitles:  f.person_job_title?.include              ?? [],
      exactMatch: f.person_job_title?.match_only_exact_job_titles ?? false,
      seniority:  f.person_seniority?.include              ?? [],
      industries: f.company_industry?.include              ?? [],
      headcount:  f.company_headcount_range                ?? [],
      countries:  f.person_country?.include                ?? [],
    };
  } catch { return { ...DEFAULT_FILTERS }; }
}

// ─── Small tag-list editor ────────────────────────────────────────────────────
function TagEditor({ label, values, onChange, placeholder }: {
  label: string; values: string[];
  onChange: (v: string[]) => void; placeholder?: string;
}) {
  const [input, setInput] = useState('');
  const inputStyle: CSSProperties = {
    background: '#0a1120', border: '1px solid #1e2d42', borderRadius: 7,
    padding: '7px 11px', color: '#e2e8f0', fontSize: 12,
    fontFamily: 'Sora, sans-serif', outline: 'none', flex: 1, minWidth: 120,
  };
  function add() {
    const v = input.trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setInput('');
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 12, fontWeight: 500, color: '#94a3b8' }}>{label}</label>
      <div style={{ display: 'flex', gap: 6 }}>
        <input value={input} placeholder={placeholder} style={inputStyle}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
        <Btn size="sm" onClick={add}>Add</Btn>
      </div>
      {values.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {values.map(v => (
            <span key={v} style={{
              background: '#0c1a3d', border: '1px solid #1d4ed8',
              borderRadius: 5, padding: '2px 8px', fontSize: 11,
              color: '#93c5fd', display: 'flex', alignItems: 'center', gap: 5,
            }}>
              {v}
              <span onClick={() => onChange(values.filter(x => x !== v))}
                style={{ cursor: 'pointer', color: '#475569', lineHeight: 1 }}>×</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Checkbox row multi-select ─────────────────────────────────────────────
function CheckboxGroup({ label, options, values, onChange }: {
  label: string; options: string[];
  values: string[]; onChange: (v: string[]) => void;
}) {
  function toggle(o: string) {
    onChange(values.includes(o) ? values.filter(x => x !== o) : [...values, o]);
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 12, fontWeight: 500, color: '#94a3b8' }}>{label}</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {options.map(o => {
          const on = values.includes(o);
          return (
            <span key={o} onClick={() => toggle(o)} style={{
              cursor: 'pointer',
              background: on ? '#0c1a3d' : 'transparent',
              border: `1px solid ${on ? '#1d4ed8' : '#1e2d42'}`,
              borderRadius: 5, padding: '3px 9px', fontSize: 11,
              color: on ? '#93c5fd' : '#475569',
              userSelect: 'none',
              transition: 'all 0.1s',
            }}>
              {o}
            </span>
          );
        })}
      </div>
    </div>
  );
}

type Audience = Schema['Audience']['type'];
type Client   = Schema['Client']['type'];

export default function AudienceBuilder() {
  const db = useMemo(() => generateClient<Schema>(), []);
  const [audiences,    setAudiences]    = useState<Audience[]>([]);
  const [clients,      setClients]      = useState<Client[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [modal,        setModal]        = useState(false);
  const [editAud,      setEditAud]      = useState<Audience | null>(null);
  const [syncing,      setSyncing]      = useState<string | null>(null);
  const [setupLoading, setSetupLoading] = useState<string | null>(null); // "${audienceId}-${platform}"
  const [toast,        setToast]        = useState<{ msg: string; type: 'success'|'error' } | null>(null);
  const [filterClient, setFilterClient] = useState('');
  const [filters,      setFilters]      = useState<Filters>({ ...DEFAULT_FILTERS });

  const [form, setForm] = useState({
    clientId:     '',
    audienceName: '',
    audienceType: 'seed' as 'seed' | 'direct',
    source:       'prospeo' as 'pixel' | 'prospeo' | 'manual',
  });

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [aRes, cRes] = await Promise.all([
        db.models.Audience.list(),
        db.models.Client.list(),
      ]);
      const sorted = [...(aRes.data ?? [])].sort((a: any, b: any) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setAudiences(sorted);
      setClients((cRes.data ?? []).filter((c: any) => c.active));
    } catch (e: any) {
      showToast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }

  function showToast(msg: string, type: 'success'|'error' = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  function openCreate() {
    setEditAud(null);
    setForm({ clientId: '', audienceName: '', audienceType: 'seed', source: 'prospeo' });
    setFilters({ ...DEFAULT_FILTERS });
    setModal(true);
  }

  function openEdit(a: Audience) {
    setEditAud(a);
    setForm({
      clientId:     a.clientId     ?? '',
      audienceName: a.audienceName ?? '',
      audienceType: ((a as any).audienceType ?? 'seed') as 'seed' | 'direct',
      source:       ((a as any).source       ?? 'prospeo') as 'pixel' | 'prospeo' | 'manual',
    });
    setFilters(parseProspeoFilters((a as any).prospeoFilters));
    setModal(true);
  }

  function closeModal() {
    setModal(false);
    setEditAud(null);
  }

  async function createAudience() {
    if (!form.clientId)     return showToast('Select a client', 'error');
    if (!form.audienceName) return showToast('Name is required', 'error');
    const built = buildProspeoFilters(filters);
    try {
      await db.models.Audience.create({
        clientId:       form.clientId,
        audienceName:   form.audienceName,
        audienceType:   form.audienceType,
        source:         form.source,
        status:         'building',
        memberCount:    0,
        prospeoFilters: form.source === 'prospeo' ? JSON.stringify(built) : null,
      } as any);
      showToast('Audience created');
      closeModal();
      await load();
    } catch (e: any) {
      showToast(e.message, 'error');
    }
  }

  async function updateAudience() {
    if (!editAud) return;
    if (!form.audienceName) return showToast('Name is required', 'error');
    const built = buildProspeoFilters(filters);
    try {
      await db.models.Audience.update({
        id:             editAud.id,
        audienceName:   form.audienceName,
        audienceType:   form.audienceType,
        source:         form.source,
        prospeoFilters: form.source === 'prospeo' ? JSON.stringify(built) : null,
      } as any);
      showToast('Audience updated');
      closeModal();
      await load();
    } catch (e: any) {
      showToast(e.message, 'error');
    }
  }

  async function triggerSync(audience: Audience) {
    const c = clients.find(cl => cl.id === audience.clientId);
    if (!c) return showToast('Client not found', 'error');

    const isManual = (audience as any).source !== 'pixel';

    if (isManual) {
      // Collect all connected per-audience sync IDs
      const syncIds = PLATFORMS
        .map(p => (audience as any)[getSyncField(p.key)])
        .filter(Boolean) as string[];

      if (syncIds.length === 0) {
        return showToast('No platform connected yet — click a platform chip to push contacts', 'error');
      }

      setSyncing(audience.id);
      try {
        // Re-export contacts for this specific list only (audienceName == listName used during import)
        await apiPost('/contacts/export', {
          clientId: c.id,
          listName: (audience as any).audienceName,
        });
        // Trigger each connected per-audience platform sync
        await Promise.all(syncIds.map(syncId => apiPost('/sync/trigger', { syncId })));
        showToast('Contacts exported & sync triggered');
        await db.models.Audience.update({
          id: audience.id,
          status: 'syncing',
          lastSyncedAt: new Date().toISOString(),
        });
        await load();
      } catch (e: any) {
        showToast(e.message, 'error');
      } finally {
        setSyncing(null);
      }
      return;
    }

    // Pixel audiences: trigger Hightouch directly (CSV already managed by nightly builder)
    if (!c.hightouchSyncIdPixel) return showToast('No Hightouch sync ID configured for this client', 'error');

    setSyncing(audience.id);
    try {
      await apiPost('/sync/trigger', { syncId: c.hightouchSyncIdPixel });
      showToast('Hightouch sync triggered');
      await db.models.Audience.update({
        id: audience.id,
        status: 'syncing',
        lastSyncedAt: new Date().toISOString(),
      });
      await load();
    } catch (e: any) {
      showToast(e.message, 'error');
    } finally {
      setSyncing(null);
    }
  }

  async function deleteAudience(id: string) {
    if (!confirm('Delete this audience? This cannot be undone.')) return;
    try {
      await db.models.Audience.delete({ id });
      showToast('Audience deleted');
      await load();
    } catch (e: any) {
      showToast(e.message, 'error');
    }
  }

  // Per-platform Hightouch sync setup for a specific audience
  const PLATFORMS = [
    { key: 'meta',     label: 'M',  fullLabel: 'Meta'     },
    { key: 'google',   label: 'G',  fullLabel: 'Google'   },
    { key: 'tiktok',   label: 'TT', fullLabel: 'TikTok'   },
    { key: 'twitter',  label: 'X',  fullLabel: 'X Ads'    },
    { key: 'linkedin', label: 'LI', fullLabel: 'LinkedIn' },
  ] as const;

  function getSyncField(platform: string) {
    return `hightouchSyncId${platform.charAt(0).toUpperCase()}${platform.slice(1)}` as keyof Audience;
  }

  async function handleSetupSync(audience: Audience, platform: string) {
    const key = `${audience.id}-${platform}`;
    const existingId = (audience as any)[getSyncField(platform)];
    if (existingId) {
      showToast(`Sync #${existingId} already active for ${platform}`);
      return;
    }
    setSetupLoading(key);
    try {
      const res = await apiPost<{ syncId: string }>('/hightouch/setup', {
        clientId:    audience.clientId,
        type:        platform,
        audienceId:  audience.id,
        s3KeyOverride: (audience as any).s3ExportKey ?? undefined,
      });
      showToast(`Hightouch ${platform} sync #${res.syncId} created`);
      await load();
    } catch (e: any) {
      showToast(e.message, 'error');
    } finally {
      setSetupLoading(null);
    }
  }

  const clientMap  = Object.fromEntries(clients.map(c => [c.id, c]));
  const filtered   = audiences.filter(a => !filterClient || a.clientId === filterClient);
  const liveCount  = audiences.filter(a => a.status === 'live').length;
  const totalMembers = audiences.reduce((s, a) => s + (a.memberCount ?? 0), 0);
  const pixelCount = audiences.filter((a: any) => a.source === 'pixel').length;

  return (
    <div>
      <SectionHeader
        title="Audiences"
        sub={`${audiences.length} total · ${liveCount} live`}
        action={<Btn onClick={openCreate}>+ New Audience</Btn>}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
        <Stat label="Total Audiences" value={audiences.length} />
        <Stat label="Total Members"   value={totalMembers}     accent />
        <Stat label="Pixel Sourced"   value={pixelCount}       />
      </div>

      <div style={{ marginBottom: 16, maxWidth: 280 }}>
        <Select
          label="Filter by client"
          value={filterClient}
          onChange={e => setFilterClient(e.target.value)}
          options={[
            { value: '', label: 'All clients' },
            ...clients.map(c => ({ value: c.id, label: c.clientName ?? c.id })),
          ]}
        />
      </div>

      <Card style={{ padding: 0 }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#475569' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <Empty
            icon="◎"
            title="No audiences yet"
            description="Create an audience and run the pipeline to populate it"
            action={<Btn onClick={openCreate}>+ New Audience</Btn>}
          />
        ) : (
          <Table
            headers={['Audience', 'Client', 'Source', 'Type', 'Members', 'Status', 'Platforms', 'Actions']}
            rows={filtered.map(a => [
              <div key="name">
                <div style={{ fontWeight: 500, color: '#e2e8f0', fontSize: 13 }}>{a.audienceName}</div>
                {a.lastBuiltAt && (
                  <div style={{ fontSize: 11, color: '#475569', marginTop: 1 }}>
                    Built {new Date(a.lastBuiltAt).toLocaleDateString()}
                  </div>
                )}
              </div>,
              <span key="client" style={{ fontSize: 12, color: '#64748b' }}>
                {clientMap[a.clientId ?? '']?.clientName ?? '—'}
              </span>,
              <Badge key="src"    label={(a as any).source      ?? 'unknown'} />,
              <Badge key="type"   label={(a as any).audienceType ?? 'seed'}   />,
              <span key="members" style={{ fontFamily: 'DM Mono, monospace', fontSize: 13, color: '#93c5fd' }}>
                {(a.memberCount ?? 0).toLocaleString()}
              </span>,
              <Badge key="status" label={a.status ?? 'building'} />,
              <div key="syncs" style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                {(a as any).source !== 'pixel' && (
                  <span title="Use as lookalike seed only — not for direct targeting" style={{
                    background: '#1c1400', border: '1px solid #78350f',
                    borderRadius: 4, padding: '2px 7px', fontSize: 10,
                    color: '#fbbf24', cursor: 'default',
                  }}>seed</span>
                )}
                {PLATFORMS.map(p => {
                  const syncId = (a as any)[getSyncField(p.key)];
                  const loadKey = `${a.id}-${p.key}`;
                  const isLoading = setupLoading === loadKey;
                  return syncId ? (
                    <span key={p.key} title={`✓ ${p.fullLabel} connected — use Push Data to re-sync`} style={{
                      background: '#052e16', border: '1px solid #15803d',
                      borderRadius: 4, padding: '2px 7px', fontSize: 11,
                      color: '#4ade80', fontWeight: 600, cursor: 'default',
                    }}>✓ {p.fullLabel}</span>
                  ) : (
                    <span key={p.key} title={`Click to push ${(a.memberCount ?? 0).toLocaleString()} contacts to ${p.fullLabel} — creates the audience and syncs immediately`} onClick={() => !isLoading && handleSetupSync(a, p.key)} style={{
                      background: 'transparent', border: '1px solid #1e2d42',
                      borderRadius: 4, padding: '2px 7px', fontSize: 11,
                      color: isLoading ? '#93c5fd' : '#475569',
                      cursor: isLoading ? 'wait' : 'pointer',
                      transition: 'all 0.15s',
                    }}>
                      {isLoading ? `Connecting ${p.fullLabel}…` : p.fullLabel}
                    </span>
                  );
                })}
              </div>,
              <div key="actions" style={{ display: 'flex', gap: 6 }}>
                <Btn size="sm" variant="secondary" onClick={() => openEdit(a)}>Edit</Btn>
                <Btn size="sm" variant="secondary" loading={syncing === a.id} onClick={() => triggerSync(a)} title="Push contacts to connected platforms">Push Data</Btn>
                <Btn size="sm" variant="danger" onClick={() => deleteAudience(a.id)}>Del</Btn>
              </div>,
            ])}
          />
        )}
      </Card>

      <div style={{
        marginTop: 14, padding: '10px 14px',
        background: '#0c1a3d', border: '1px solid #1d4ed8',
        borderRadius: 8, fontSize: 12, color: '#93c5fd',
      }}>
        <strong>Grey chip</strong> = click to push contacts to that platform (one-time setup + sync, takes ~1 min).
        <strong style={{ color: '#4ade80' }}> Green ✓</strong> = already connected — use <strong>Push Data</strong> to re-sync with updated contacts.
        Seed audiences are for <strong>lookalike targeting only</strong> — not for direct ads.
      </div>

      <Modal open={modal} onClose={closeModal} title={editAud ? 'Edit Audience' : 'New Audience'} width={560}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* ── Basic fields ── */}
          {!editAud && (
            <Select
              label="Client *"
              value={form.clientId}
              onChange={e => setForm(f => ({ ...f, clientId: e.target.value }))}
              options={[
                { value: '', label: 'Select a client…' },
                ...clients.map(c => ({ value: c.id, label: c.clientName ?? c.id })),
              ]}
            />
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 500, color: '#94a3b8' }}>Audience name *</label>
            <input
              value={form.audienceName}
              onChange={e => setForm(f => ({ ...f, audienceName: e.target.value }))}
              placeholder="Healthcare Decision Makers — Q2 2026"
              style={{
                background: '#0a1120', border: '1px solid #1e2d42', borderRadius: 7,
                padding: '8px 12px', color: '#e2e8f0', fontSize: 13,
                fontFamily: 'Sora, sans-serif', outline: 'none', width: '100%', boxSizing: 'border-box',
              }}
            />
            <span style={{ fontSize: 11, color: '#475569' }}>Use neutral names — avoid mentioning data source</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Select
              label="Source"
              value={form.source}
              onChange={e => setForm(f => ({ ...f, source: e.target.value as any }))}
              options={[
                { value: 'prospeo', label: 'Prospeo (ICP search)' },
                { value: 'pixel',   label: 'Pixel (site visitors)' },
                { value: 'manual',  label: 'Manual upload' },
              ]}
            />
            <Select
              label="Audience type"
              value={form.audienceType}
              onChange={e => setForm(f => ({ ...f, audienceType: e.target.value as any }))}
              options={[
                { value: 'seed',   label: 'Seed (Lookalike)' },
                { value: 'direct', label: 'Direct (consented)' },
              ]}
            />
          </div>

          {/* ── Prospeo filter builder ── */}
          {form.source === 'prospeo' && (
            <>
              <div style={{
                fontSize: 11, fontWeight: 600, color: '#3b82f6',
                letterSpacing: '0.08em', textTransform: 'uppercase',
                paddingTop: 4, borderTop: '1px solid #111927',
              }}>
                Prospeo Search Filters
              </div>

              <TagEditor
                label="Job titles (press Enter or click Add)"
                values={filters.jobTitles}
                onChange={v => setFilters(f => ({ ...f, jobTitles: v }))}
                placeholder="e.g. VP of Marketing"
              />

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  id="exactMatch"
                  checked={filters.exactMatch}
                  onChange={e => setFilters(f => ({ ...f, exactMatch: e.target.checked }))}
                  style={{ accentColor: '#3b82f6' }}
                />
                <label htmlFor="exactMatch" style={{ fontSize: 12, color: '#94a3b8', cursor: 'pointer' }}>
                  Exact job title match only
                </label>
              </div>

              <CheckboxGroup
                label="Seniority"
                options={SENIORITY_OPTIONS}
                values={filters.seniority}
                onChange={v => setFilters(f => ({ ...f, seniority: v }))}
              />

              <TagEditor
                label="Industries"
                values={filters.industries}
                onChange={v => setFilters(f => ({ ...f, industries: v }))}
                placeholder="e.g. Healthcare, Software Development"
              />

              <CheckboxGroup
                label="Company headcount"
                options={HEADCOUNT_OPTIONS}
                values={filters.headcount}
                onChange={v => setFilters(f => ({ ...f, headcount: v }))}
              />

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 500, color: '#94a3b8' }}>Countries</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {COUNTRY_OPTIONS.map(({ code, label }) => {
                    const on = filters.countries.includes(code);
                    return (
                      <span key={code} onClick={() => setFilters(f => ({
                        ...f,
                        countries: on
                          ? f.countries.filter(x => x !== code)
                          : [...f.countries, code],
                      }))} style={{
                        cursor: 'pointer',
                        background: on ? '#0c1a3d' : 'transparent',
                        border: `1px solid ${on ? '#1d4ed8' : '#1e2d42'}`,
                        borderRadius: 5, padding: '3px 9px', fontSize: 11,
                        color: on ? '#93c5fd' : '#475569',
                        userSelect: 'none',
                      }}>
                        {code} · {label}
                      </span>
                    );
                  })}
                </div>
              </div>

              {Object.keys(buildProspeoFilters(filters)).length === 0 && (
                <div style={{ fontSize: 11, color: '#f59e0b', background: '#1c1200',
                              border: '1px solid #78350f', borderRadius: 7, padding: '8px 12px' }}>
                  ⚠ No filters set — add at least one filter before running the pipeline.
                </div>
              )}
            </>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 8, borderTop: '1px solid #111927' }}>
            <Btn variant="secondary" onClick={closeModal}>Cancel</Btn>
            <Btn onClick={editAud ? updateAudience : createAudience}>
              {editAud ? 'Save Changes' : 'Create Audience'}
            </Btn>
          </div>
        </div>
      </Modal>

      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
